import {
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { VENCIMIENTOS, type FiltroVencimiento } from "@uirtus/shared";
import { ClienteDetalle } from "./rutas/cliente-detalle";
import { Clientes } from "./rutas/clientes";
import { CotizacionDetalle } from "./rutas/cotizacion-detalle";
import { CotizacionNueva } from "./rutas/cotizacion-nueva";
import { Cotizaciones } from "./rutas/cotizaciones";
import { DominioDetalle } from "./rutas/dominio-detalle";
import { Dominios } from "./rutas/dominios";
import { Facturas } from "./rutas/facturas";
import { Login } from "./rutas/login";
import { PagoDetalle } from "./rutas/pago-detalle";
import { PagoNuevo } from "./rutas/pago-nuevo";
import { Pagos } from "./rutas/pagos";
import { ProyectoDetalle } from "./rutas/proyecto-detalle";
import { Proyectos } from "./rutas/proyectos";
import { Raiz } from "./rutas/raiz";
import { Servicios } from "./rutas/servicios";
import { Tablero } from "./rutas/tablero";

/**
 * Rutas definidas en código, no por archivos.
 *
 * El plugin de rutas por archivo genera un `routeTree.gen.ts` que hay que
 * tener compilado antes de poder hacer typecheck. En un panel de cinco
 * pantallas no compensa esa dependencia de orden.
 *
 * Cada ruta se declara por separado a propósito: así TypeScript conserva el
 * tipo literal del path y `<Link to="/facturas">` queda verificado en
 * compilación. Un helper que reciba `path: string` rompe esa garantía.
 */
const rutaRaiz = createRootRoute({ component: Raiz });

const rutaLogin = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/login",
  component: Login,
});

const rutaTablero = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/",
  component: Tablero,
});

const rutaClientes = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/clientes",
  component: Clientes,
});

const rutaClienteDetalle = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/clientes/$id",
  component: ClienteDetalle,
});

const rutaServicios = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/servicios",
  component: Servicios,
});

const rutaProyectos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/proyectos",
  component: Proyectos,
});

const rutaProyectoDetalle = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/proyectos/$id",
  component: ProyectoDetalle,
});

const rutaCotizaciones = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/cotizaciones",
  component: Cotizaciones,
});

// Antes que la ruta dinámica $id: un segmento estático como "nueva" tiene
// que resolver primero o TanStack Router intentaría tratarlo como un id.
const rutaCotizacionNueva = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/cotizaciones/nueva",
  component: CotizacionNueva,
});

const rutaCotizacionDetalle = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/cotizaciones/$id",
  component: CotizacionDetalle,
});

const rutaFacturas = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/facturas",
  component: Facturas,
});

const rutaPagos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/pagos",
  component: Pagos,
});

// Igual que con cotizaciones: "nueva" antes que la ruta dinámica $id.
const rutaPagoNuevo = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/pagos/nuevo",
  component: PagoNuevo,
});

const rutaPagoDetalle = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/pagos/$id",
  component: PagoDetalle,
});

const rutaDominios = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/dominios",
  component: Dominios,
  // Los indicadores del tablero enlazan aquí ya filtrados.
  validateSearch: (
    s: Record<string, unknown>,
  ): { vencimiento?: FiltroVencimiento; dias?: number } => {
    const vencimiento = VENCIMIENTOS.find((v) => v === s["vencimiento"]);
    const dias = Number(s["dias"]);
    return {
      ...(vencimiento && vencimiento !== "todos" ? { vencimiento } : {}),
      ...(Number.isInteger(dias) && dias >= 1 && dias <= 365 ? { dias } : {}),
    };
  },
});

const rutaDominioDetalle = createRoute({
  getParentRoute: () => rutaRaiz,
  path: "/dominios/$id",
  component: DominioDetalle,
});

const arbolDeRutas = rutaRaiz.addChildren([
  rutaLogin,
  rutaTablero,
  rutaClientes,
  rutaClienteDetalle,
  rutaServicios,
  rutaProyectos,
  rutaProyectoDetalle,
  rutaCotizaciones,
  rutaCotizacionNueva,
  rutaCotizacionDetalle,
  rutaFacturas,
  rutaPagos,
  rutaPagoNuevo,
  rutaPagoDetalle,
  rutaDominios,
  rutaDominioDetalle,
]);

export const router = createRouter({
  routeTree: arbolDeRutas,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
